import { Injectable, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';
import { NativeAudio } from '@capacitor-community/native-audio';

type SoundKey = 'in' | 'out' | 'error' | 'audio_message_start' | 'audio_message_end';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private initialized = false;
  private userInteracted = false;
  private initPromise: Promise<void> | null = null;

  private audioContext?: AudioContext;
  private currentMp3Source?: AudioBufferSourceNode;
  private mediaStreamDestination?: MediaStreamAudioDestinationNode;
  public currentMp3Stream?: MediaStream; // Public for WebRTC

  constructor(private platform: Platform, private ngZone: NgZone) {
    const initOnInteraction = () => {
      if (this.userInteracted) return;
      this.userInteracted = true;
      document.removeEventListener('touchstart', initOnInteraction);
      document.removeEventListener('click', initOnInteraction);
      void this.initializeNativeAudio();
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(e => console.error('AudioContext resume failed:', e));
      }
    };

    document.addEventListener('touchstart', initOnInteraction, { once: true, passive: true });
    document.addEventListener('click', initOnInteraction, { once: true, passive: true });
  }

  private async initializeNativeAudio(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.doInitializeNativeAudio();
    return this.initPromise;
  }

  private async doInitializeNativeAudio(): Promise<void> {
    try {
      console.log('[AudioService] Initializing Native Audio system');

      let basePath = '';
      if (this.platform.is('ios')) {
        basePath = 'public/assets/sounds';
      } else if (this.platform.is('android')) {
        basePath = 'public/assets/sounds';
      } else {
        basePath = 'assets/sounds';
      }

      console.log('[AudioService] Native Audio base path:', basePath);

      const audioFiles = [
        { id: 'in', files: [`${basePath}/in.mp3`, `${basePath}/in.m4a`, 'assets/sounds/in.mp3', 'assets/sounds/in.m4a'] },
        { id: 'out', files: [`${basePath}/out.mp3`, `${basePath}/out.m4a`, 'assets/sounds/out.mp3', 'assets/sounds/out.m4a'] },
        { id: 'error', files: [`${basePath}/error.mp3`, `${basePath}/error.m4a`, 'assets/sounds/error.mp3', 'assets/sounds/error.m4a'] },
        {
          id: 'audio_message_start',
          files: [`${basePath}/audio_message_start.mp3`, 'assets/sounds/audio_message_start.mp3']
        },
        {
          id: 'audio_message_end',
          files: [`${basePath}/audio_message_end.mp3`, 'assets/sounds/audio_message_end.mp3']
        }
      ];

      for (const audioFile of audioFiles) {
        await this.preloadWithFallback(audioFile.id, audioFile.files);
      }

      this.initialized = true;
      console.log('[AudioService] Native Audio system initialized successfully');
    } catch (error) {
      console.error('[AudioService] Failed to initialize Native Audio system:', error);
      this.initialized = true;
    }
  }

  private async preloadWithFallback(assetId: string, paths: string[]): Promise<void> {
    // Unload first to avoid "asset already exists" errors from a prior partial load
    try { await NativeAudio.unload({ assetId }); } catch { /* not loaded yet, ignore */ }

    for (const filePath of paths) {
      try {
        await NativeAudio.preload({ assetId, assetPath: filePath, isUrl: false });
        console.log(`[AudioService] Loaded ${assetId} from ${filePath}`);
        return;
      } catch (error) {
        console.warn(`[AudioService] Failed to load ${assetId} from ${filePath}:`, error);
      }
    }
    console.error(`[AudioService] Could not load ${assetId} from any path`);
  }

  private async ensureNativeAudioInitialized(): Promise<boolean> {
    if (!this.userInteracted) {
      console.warn('[AudioService] Waiting for user interaction before initializing Native Audio');
      return false;
    }

    if (!this.initialized) {
      await this.initializeNativeAudio();
    }

    return true;
  }

  public async playInSound(): Promise<void> {
    await this.forcePlayNativeSound('in');
  }

  public async playOutSound(): Promise<void> {
    await this.forcePlayNativeSound('out');
  }

  public async playErrorSound(): Promise<void> {
    await this.forcePlayNativeSound('error');
  }

  public async testSound(): Promise<void> {
    console.log('[AudioService] Testing Native Audio system');
    try {
      if (!this.userInteracted) {
        this.userInteracted = true;
        await this.initializeNativeAudio();
      }

      await this.playInSound();
      await new Promise(resolve => setTimeout(resolve, 1000));
      await this.playOutSound();
      await new Promise(resolve => setTimeout(resolve, 1000));
      await this.playErrorSound();

      console.log('[AudioService] Native Audio test complete');
    } catch (error) {
      console.error('[AudioService] Native Audio test failed:', error);
    }
  }

  public async forcePlayNativeSound(key: SoundKey): Promise<void> {
    this.userInteracted = true;

    if (!this.initialized) {
      await this.initializeNativeAudio();
    }

    try {
      await NativeAudio.play({
        assetId: key
      });
    } catch (error) {
      console.error(`[AudioService] Failed to play Native Audio sound ${key}:`, error);
    }
  }

  async playSound(soundName: string): Promise<void> {
    await this.forcePlayNativeSound(soundName as SoundKey);
  }

  public async playToneAsset(fileName: string): Promise<void> {
    const paths = this.platform.is('hybrid')
      ? [`public/assets/sounds/${fileName}`, `assets/sounds/${fileName}`]
      : [`assets/sounds/${fileName}`];

    for (const path of paths) {
      const played = await this.tryPlayTonePath(path);
      if (played) {
        return;
      }
    }
  }

  private async tryPlayTonePath(path: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      (audio as any).playsInline = true;
      audio.loop = false;

      let settled = false;
      const finish = (success: boolean) => {
        if (settled) return;
        settled = true;
        audio.pause();
        audio.src = '';
        resolve(success);
      };

      const timeout = window.setTimeout(() => finish(false), 2000);

      audio.onended = () => {
        clearTimeout(timeout);
        finish(true);
      };
      audio.onerror = () => {
        clearTimeout(timeout);
        finish(false);
      };

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          clearTimeout(timeout);
          finish(false);
        });
      }
    });
  }

  /**
   * Starts broadcasting an MP3 file as a MediaStream.
   * @param audioFilePath The path to the MP3 file (e.g., 'assets/sounds/music.mp3').
   * @returns The MediaStream containing the audio, or undefined if failed.
   */
  public async startMp3Broadcast(audioFilePath: string): Promise<MediaStream | undefined> {
    // Ensure user interaction has happened before creating AudioContext
    if (!this.userInteracted) {
      console.warn('[AudioService] Cannot start MP3 broadcast: user interaction required to enable Web Audio API.');
      // You might want to show a UI prompt here for the user to tap/click something
      return undefined;
    }

    // Create or resume AudioContext
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log('[AudioService] Web Audio API AudioContext created.');
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      console.log('[AudioService] Web Audio API AudioContext resumed.');
    }

    // Stop any existing MP3 broadcast before starting a new one
    this.stopMp3Broadcast();

    try {
      const response = await fetch(audioFilePath);
      const arrayBuffer = await response.arrayBuffer();

      // Decode audio data outside Angular zone for performance
      const audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
        this.audioContext!.decodeAudioData(arrayBuffer, resolve, reject);
      });

      this.currentMp3Source = this.audioContext!.createBufferSource();
      this.currentMp3Source.buffer = audioBuffer;
      this.currentMp3Source.loop = true; // Loop the MP3 for continuous broadcast

      this.mediaStreamDestination = this.audioContext!.createMediaStreamDestination();

      // Connect the source to both the local speakers and the MediaStreamDestination
      this.currentMp3Source.connect(this.audioContext!.destination);
      this.currentMp3Source.connect(this.mediaStreamDestination);

      this.currentMp3Stream = this.mediaStreamDestination.stream;

      // Start playback (starts at time 0)
      this.currentMp3Source.start(0);

      console.log(`[AudioService] Started MP3 broadcast from ${audioFilePath}.`);
      return this.currentMp3Stream;

    } catch (error) {
      console.error(`[AudioService] Failed to start MP3 broadcast from ${audioFilePath}:`, error);
      this.stopMp3Broadcast(); // Ensure cleanup on failure
      return undefined;
    }
  }

  public async resumeAudioContext(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (e) {
        console.error('[AudioService] AudioContext resume failed on foreground:', e);
      }
    }
  }

  /**
   * Stops any active MP3 broadcast and cleans up resources.
   */
  public stopMp3Broadcast(): void {
    if (this.currentMp3Source) {
      try {
        this.currentMp3Source.stop();
        this.currentMp3Source.disconnect();
      } catch (e) {
        console.warn('[AudioService] Error stopping or disconnecting MP3 source node (may already be stopped):', e);
      }
      this.currentMp3Source = undefined;
      console.log('[AudioService] Stopped MP3 source node.');
    }

    if (this.mediaStreamDestination) {
      this.mediaStreamDestination.disconnect();
      this.mediaStreamDestination = undefined;
      console.log('[AudioService] Disconnected MediaStreamDestination node.');
    }

    if (this.currentMp3Stream) {
      this.currentMp3Stream.getTracks().forEach(track => track.stop());
      this.currentMp3Stream = undefined;
      console.log('[AudioService] Stopped and cleared current MP3 stream.');
    }
  }
} 